/**
 * LearnModeOverlay — the ONE Learn-Mode badge + popover implementation shared by
 * portal, medspa and medspa-patient (merged 2026-10-09 from three forks).
 *
 * Rendered by each app's `LearnModeIsland` while Learn Mode is ON. Puts a "?"
 * badge on every `[data-help-context]` element (and any caller-supplied anchor,
 * e.g. portal tour steps) that has a tip; clicking a badge opens a popover.
 *
 * Badge placement (see `LearnBadgePlacement`):
 *   - `anchored` — badge portaled INTO the target (promoted to
 *     `position: relative` when static; z-lifted while open). Scrolls natively.
 *   - `floating` — badges in one body-level layer, `position: fixed`, clamped to
 *     the viewport, re-measured on scroll/resize (rAF-throttled, capture phase
 *     so inner scrollers such as FullCalendar count too).
 *
 * The popover is ALWAYS a `position: fixed` portal on `document.body`, placed by
 * `popoverPosition()` next to its badge — never clipped by an ancestor's
 * `overflow: hidden`, never trapped in a sibling stacking context. It mounts at
 * opacity 0, is measured in a layout effect, then shown (opacity — not
 * visibility — so it stays in the accessibility tree while it settles).
 *
 * Dismissal: Escape, a mousedown outside the popover and every badge, the
 * optional close button, clicking the open badge again, or (`openOnHover`)
 * leaving the popover.
 */

import {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLearnAffordances } from "./affordances";
import { computeBadgePlacement, REDUCED_SIZE_SCALE } from "./badgePlacement";
import { createRectFreeChecker } from "./collectObstacleRects";
import { BADGE_SIZE, floatingBadgePosition, type Point, popoverPosition, popoverWidthFor } from "./geometry";
import {
  type LearnAffordance,
  type LearnModeClassNames,
  type LearnModeOverlayProps,
  type LearnTip,
  TOOLTIP_Z_INDEX,
} from "./types";

/** Marks badge buttons so an outside-click never counts a badge as "outside". */
const BADGE_ATTR = "data-learn-badge";

/** Trailing debounce for mutation-triggered obstacle re-measures (`avoidObstacles`). */
const MUTATION_REMEASURE_DEBOUNCE_MS = 150;

/** Resolved obstacle-aware spot for one badge (`avoidObstacles`). */
interface ObstaclePlacement {
  top: number;
  left: number;
  reduced: boolean;
}

function samePlacements(
  a: ReadonlyMap<string, ObstaclePlacement | null>,
  b: ReadonlyMap<string, ObstaclePlacement | null>
): boolean {
  if (a.size !== b.size) return false;
  for (const [key, pa] of a) {
    if (!b.has(key)) return false;
    const pb = b.get(key);
    if (pa === null || pb === null || pa === undefined || pb === undefined) {
      if ((pa ?? null) !== (pb ?? null)) return false;
      continue;
    }
    if (pa.top !== pb.top || pa.left !== pb.left || pa.reduced !== pb.reduced) return false;
  }
  return true;
}

function defaultBadgeLabel(affordance: LearnAffordance): string {
  return `Help: ${affordance.tip.title}`;
}

function defaultStartTour(pageKey: string): void {
  window.dispatchEvent(new CustomEvent("plerity:start-tour", { detail: { pageKey } }));
}

function defaultOpenHelp(): void {
  window.dispatchEvent(new CustomEvent("plerity:open-help"));
}

/** DOM-id-safe popover id for an affordance key. */
export function popoverIdFor(key: string): string {
  return `help-pop-${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function viewport(): { width: number; height: number } {
  return { width: window.innerWidth || 1024, height: window.innerHeight || 768 };
}

function join(...parts: Array<string | undefined | false>): string {
  return parts.filter(Boolean).join(" ");
}

/* ── Anchored badge host ────────────────────────────────────────────────── */

interface AnchoredBadgeHostProps {
  target: HTMLElement;
  isOpen: boolean;
  lift?: string;
  anchorClassName?: string;
  children: ReactNode;
}

function AnchoredBadgeHost({ target, isOpen, lift, anchorClassName, children }: AnchoredBadgeHostProps): ReactElement {
  // Promote the target to a positioned ancestor so the absolute badge anchors to
  // it — only when it is not one already (jsdom reports "" for unset; browsers
  // "static"). Restored on unmount.
  useEffect(() => {
    const computed = window.getComputedStyle(target).position;
    if (["relative", "absolute", "fixed", "sticky"].includes(computed)) return;
    const prev = target.style.position;
    target.style.position = "relative";
    return () => {
      target.style.position = prev;
    };
  }, [target]);

  // While open, lift the target above later sibling cards / fixed chrome.
  useEffect(() => {
    if (!isOpen || !lift) return;
    const prev = target.style.zIndex;
    target.style.zIndex = lift;
    return () => {
      target.style.zIndex = prev;
    };
  }, [isOpen, lift, target]);

  return createPortal(
    <span className={anchorClassName} data-plerity-learn-badge="">
      {children}
    </span>,
    target
  );
}

/* ── Popover body ───────────────────────────────────────────────────────── */

function PopoverContent({
  tip,
  titleId,
  classNames,
  renderBody,
  showCloseButton,
  onClose,
  onTour,
  onWatch,
}: {
  tip: LearnTip;
  titleId: string;
  classNames: LearnModeClassNames;
  renderBody?: (tip: LearnTip) => ReactNode;
  showCloseButton: boolean;
  onClose: () => void;
  onTour: () => void;
  onWatch: () => void;
}): ReactElement {
  const rich = renderBody?.(tip);
  const hasRich = rich !== null && rich !== undefined && rich !== false;
  const bullets = tip.bullets ?? [];

  return (
    <>
      <div className={classNames.header}>
        <p id={titleId} className={classNames.title}>
          {tip.title}
        </p>
        {showCloseButton && (
          <button type="button" aria-label="Close help tip" onClick={onClose} className={classNames.closeButton}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {hasRich ? (
        <div className={classNames.richBody ?? classNames.body}>{rich}</div>
      ) : tip.body ? (
        <p className={classNames.body}>{tip.body}</p>
      ) : null}

      {bullets.length > 0 && (
        <ul className={classNames.bulletList}>
          {bullets.map((bullet) => (
            <li key={bullet} className={classNames.bulletItem}>
              {classNames.bulletMarker !== undefined ? (
                <>
                  <span aria-hidden="true" className={classNames.bulletMarker}>
                    &bull;
                  </span>
                  <span>{bullet}</span>
                </>
              ) : (
                bullet
              )}
            </li>
          ))}
        </ul>
      )}

      {(tip.tourAction || tip.videoAction) && (
        <div className={classNames.actions}>
          {tip.tourAction && (
            <button type="button" onClick={onTour} className={classNames.tourButton}>
              Start tour
            </button>
          )}
          {tip.videoAction && (
            <button type="button" onClick={onWatch} className={classNames.videoButton}>
              Watch
            </button>
          )}
        </div>
      )}
    </>
  );
}

/* ── LearnModeOverlay ───────────────────────────────────────────────────── */

export function LearnModeOverlay(props: LearnModeOverlayProps): ReactElement | null {
  const {
    pageKey,
    placement,
    classNames,
    zIndex,
    renderBody,
    badgeLabel = defaultBadgeLabel,
    showCloseButton = true,
    openOnHover = false,
    onStartTour = defaultStartTour,
    onOpenHelp = defaultOpenHelp,
    avoidObstacles = false,
    obstacleOptions,
  } = props;

  const avoid = avoidObstacles && placement === "floating";
  const tooltipLayer = zIndex.layer === "tooltip";
  const badgeZ = zIndex.badge ?? (tooltipLayer ? TOOLTIP_Z_INDEX : undefined);
  const popoverZ = zIndex.popover ?? (tooltipLayer ? TOOLTIP_Z_INDEX : undefined);

  const affordances = useLearnAffordances(props);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<Point | null>(null);
  // Bumped (rAF-throttled) on scroll / resize so floating badges and the open
  // popover recompute from live getBoundingClientRect() coordinates.
  const [tick, setTick] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const badgeRefs = useRef(new Map<string, HTMLButtonElement>());
  // `avoidObstacles` only: resolved spot per affordance key (null = no safe spot,
  // render no badge; absent = not measured yet, also no badge).
  const [placements, setPlacements] = useState<ReadonlyMap<string, ObstaclePlacement | null>>(() => new Map());
  const obstacleOptionsRef = useRef(obstacleOptions);
  obstacleOptionsRef.current = obstacleOptions;

  const open = openKey === null ? null : (affordances.find((a) => a.key === openKey) ?? null);

  // The open anchor disappeared (view switch, unmount) — close.
  useEffect(() => {
    if (openKey !== null && open === null) setOpenKey(null);
  }, [openKey, open]);

  // Escape / outside mousedown. Capture-phase keydown so a focused widget that
  // stops propagation (calendar, editor) cannot swallow the Escape. Mousedowns
  // on a badge are NOT "outside": the badge's own click toggles, and treating
  // it as outside would close-then-reopen on every click of the open badge.
  useEffect(() => {
    if (openKey === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (popoverRef.current?.contains(t)) return;
      if (t instanceof Element && t.closest(`[${BADGE_ATTR}]`)) return;
      setOpenKey(null);
    };
    document.addEventListener("keydown", onKey, { capture: true });
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey, { capture: true });
      document.removeEventListener("mousedown", onDown);
    };
  }, [openKey]);

  // Scroll / resize tracking — only while something actually needs it.
  const tracking = placement === "floating" || openKey !== null;
  useEffect(() => {
    if (!tracking) return;
    let raf = 0;
    const onMove = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setTick((n) => n + 1);
      });
    };
    window.addEventListener("scroll", onMove, { capture: true, passive: true });
    window.addEventListener("resize", onMove, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onMove, { capture: true });
      window.removeEventListener("resize", onMove);
    };
  }, [tracking]);

  // One extra measure pass after opening, so late-settling content (rich
  // bodies, fonts) is reflected in the flip-above decision.
  useEffect(() => {
    if (openKey === null) return;
    const raf = requestAnimationFrame(() => setTick((n) => n + 1));
    return () => cancelAnimationFrame(raf);
  }, [openKey]);

  // `avoidObstacles`: re-measure on DOM mutation bursts (a neighbour can slide
  // under a badge without the target changing; trailing-debounced so a polling
  // page never becomes a measurement storm) and on target resize (reflow that
  // fires neither scroll nor resize).
  useEffect(() => {
    if (!avoid) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const mutations = new MutationObserver(() => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        setTick((n) => n + 1);
      }, MUTATION_REMEASURE_DEBOUNCE_MS);
    });
    mutations.observe(document.body, { childList: true, subtree: true, characterData: true });
    let resizes: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizes = new ResizeObserver(() => setTick((n) => n + 1));
      for (const af of affordances) resizes.observe(af.target);
    }
    return () => {
      mutations.disconnect();
      resizes?.disconnect();
      if (timer !== null) clearTimeout(timer);
    };
  }, [avoid, affordances]);

  // `avoidObstacles`: resolve every badge's spot in one pass, before paint. Our
  // own badges / popover are ignored by the checker, so the previous render's
  // positions never block themselves.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` is a deliberate change-trigger (scroll/resize/mutation) — it forces a re-measure of live rects without being read here
  useLayoutEffect(() => {
    if (!avoid) return;
    const isFree = createRectFreeChecker(obstacleOptionsRef.current);
    const vp = viewport();
    const next = new Map<string, ObstaclePlacement | null>();
    for (const af of affordances) {
      if (!af.target.isConnected) {
        next.set(af.key, null);
        continue;
      }
      const result = computeBadgePlacement(af.target.getBoundingClientRect(), vp, isFree, {
        width: BADGE_SIZE,
        height: BADGE_SIZE,
      });
      next.set(af.key, result ? { top: result.top, left: result.left, reduced: result.reduced } : null);
    }
    setPlacements((prev) => (samePlacements(prev, next) ? prev : next));
  }, [avoid, affordances, tick]);

  // Place the popover next to its badge before the browser paints.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` is a deliberate change-trigger (scroll/resize/settle) — it forces a re-measure of live rects without being read here
  useLayoutEffect(() => {
    if (open === null) {
      setPopoverPos(null);
      return;
    }
    if (!open.target.isConnected) {
      setOpenKey(null);
      return;
    }
    const badge = badgeRefs.current.get(open.key);
    if (!badge) return;
    const height = popoverRef.current?.offsetHeight ?? 0;
    setPopoverPos(popoverPosition(badge.getBoundingClientRect(), height, viewport()));
  }, [open, tick]);

  if (typeof document === "undefined") return null;
  if (affordances.length === 0) return null;

  const vp = viewport();

  const renderBadge = (af: LearnAffordance, style?: CSSProperties, reduced = false): ReactElement => {
    const isOpen = openKey === af.key;
    return (
      <button
        key={af.key}
        ref={(el) => {
          if (el) badgeRefs.current.set(af.key, el);
          else badgeRefs.current.delete(af.key);
        }}
        type="button"
        data-learn-badge=""
        aria-label={badgeLabel(af)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={isOpen ? popoverIdFor(af.key) : undefined}
        onClick={(e) => {
          // A badge can sit INSIDE a clickable target (anchored placement) —
          // never let toggling the tip also activate the element behind it.
          e.stopPropagation();
          setOpenKey((prev) => (prev === af.key ? null : af.key));
        }}
        onMouseEnter={openOnHover ? () => setOpenKey(af.key) : undefined}
        className={join(
          classNames.badge,
          isOpen ? classNames.badgeActive : classNames.badgeIdle,
          reduced && classNames.badgeReduced
        )}
        style={style}
      >
        ?
      </button>
    );
  };

  let badgeLayer: ReactNode;
  if (placement === "anchored") {
    badgeLayer = affordances.map((af) => (
      <AnchoredBadgeHost
        key={af.key}
        target={af.target}
        isOpen={openKey === af.key}
        lift={zIndex.targetLift}
        anchorClassName={classNames.badgeAnchor}
      >
        {renderBadge(af)}
      </AnchoredBadgeHost>
    ));
  } else {
    const badges: ReactElement[] = [];
    for (const af of affordances) {
      if (!af.target.isConnected) continue;
      if (avoid) {
        const spot = placements.get(af.key);
        // Unmeasured, or no safe spot — a missing badge beats one on a control.
        if (!spot) continue;
        const size = spot.reduced ? BADGE_SIZE * REDUCED_SIZE_SCALE : undefined;
        badges.push(
          renderBadge(
            af,
            {
              position: "fixed",
              top: spot.top,
              left: spot.left,
              zIndex: badgeZ,
              pointerEvents: "auto",
              ...(size !== undefined ? { width: size, height: size } : {}),
            },
            spot.reduced
          )
        );
        continue;
      }
      const { top, left } = floatingBadgePosition(af.target.getBoundingClientRect(), vp);
      badges.push(renderBadge(af, { position: "fixed", top, left, zIndex: badgeZ, pointerEvents: "auto" }));
    }
    badgeLayer = createPortal(
      // Not aria-hidden — the badge buttons must stay in the accessibility tree.
      <div data-learn-overlay="" style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
        {badges}
      </div>,
      document.body
    );
  }

  const popoverId = open ? popoverIdFor(open.key) : "";
  const popover =
    open !== null &&
    createPortal(
      <div
        ref={popoverRef}
        id={popoverId}
        role="tooltip"
        aria-labelledby={`${popoverId}-title`}
        className={classNames.popover}
        style={{
          position: "fixed",
          top: popoverPos?.top ?? 0,
          left: popoverPos?.left ?? 0,
          zIndex: popoverZ,
          width: popoverWidthFor(vp),
          // Bulleted tips can run long — cap and scroll rather than overflow.
          maxHeight: "min(60vh, 420px)",
          overflowY: "auto",
          opacity: popoverPos ? 1 : 0,
        }}
        onMouseLeave={openOnHover ? () => setOpenKey(null) : undefined}
      >
        <PopoverContent
          tip={open.tip}
          titleId={`${popoverId}-title`}
          classNames={classNames}
          renderBody={renderBody}
          showCloseButton={showCloseButton}
          onClose={() => setOpenKey(null)}
          onTour={() => {
            setOpenKey(null);
            onStartTour(pageKey);
          }}
          onWatch={() => {
            setOpenKey(null);
            onOpenHelp();
          }}
        />
      </div>,
      document.body
    );

  return (
    <>
      {badgeLayer}
      {popover}
    </>
  );
}

export default LearnModeOverlay;
